import { formatChartParamsDate } from '@/pages/opinion/components/ChartCommon/formatter';
import EChartCommon from '@/pages/opinion/components/EChartCommon';
import { chartAllColors } from '@/pages/opinion/constants';
import { formatTextInfoSimpleFunc } from '@/pages/opinion/utils/chart';
import { percentageFormat, reviewsFormat } from '@/pages/opinion/utils/value';
import useBatchTopicKeyword from '@op/pages/Summary/hooks/useBatchTopicKeyword';
import { dealwithKeywordListStr } from '@op/pages/Summary/utils';
import { getPercentageInfo } from '@op/utils';
import { formatTimeToShow } from '@op/utils/time';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { getLocale } from 'umi';
import './BlockChart.scss';
import {
  useDrilldownParams,
  EDrilldownMode,
} from '@op/hooks/useDrilldownParams';
import { getFeedsStats } from '@op/services';
import { TopicLevelType } from '@op/pages/Summary/context';
import { DBO_EC } from '@op/utils/format';

export default function BlockChart({
  onClick,
  data,
  loading,
  translationData,
  formatMessage,
  transTopic,
  height = 336,
  filter = {},
  total,
  gameInfo,
  topicLevel = 'L2',
  dealwithTopic = (topic: string) => [topic],
  position = 'left',
  needToFeeds = true
}: {
  onClick?: (value: any) => void;
  data: Array<{ name: string; count: number; child: string[] }>;
  loading?: boolean;
  translationData?: any;
  formatMessage: (value: { id: string }) => string;
  transTopic?: (value: string, level?: TopicLevelType) => string;
  height?: number;
  filter?: any;
  total: number;
  gameInfo: any;
  topicLevel?: 'L1' | 'L2';
  dealwithTopic?: (topic: string) => Array<string>;
  position?: 'left' | 'right';
  needToFeeds?: boolean;
}) {
  const locale = getLocale();
  const { goPage } = useDrilldownParams();
  const translationFunc = useMemo(() => {
    if (transTopic) {
      return transTopic;
    }
    const func = formatTextInfoSimpleFunc(translationData, locale);

    return (value: string) => {
      const key = value?.toLocaleLowerCase();
      return func(key);
    };
  }, [translationData, locale, transTopic]);

  const topicList = useMemo(() => {
    return data?.map((item) => item.name);
  }, [data]);

  const { optionalKeywords, openWindows, KeywordsModalElement } =
    useBatchTopicKeyword({
      tierTopic: topicList,
      gameInfo,
      isRequest: topicLevel === 'L2',
    });

  function getTopicData(records: Array<any>) {
    const item = records[0].originTopic;
    const key = item.toLocaleLowerCase();

    const topicData = optionalKeywords[key];
    return { topicData, key };
  }

  function getLevelOneTopicChild(topicData: any, total: number) {
    const { child, itemStyle } = topicData || {};
    if (child?.length === 0) {
      return null;
    }

    const sorted = child?.sort((a, b) => b.mentions - a.mentions) || [];
    const maxValue = sorted?.[0]?.mentions || 0;
    const formatChild = sorted?.map((val) => ({
      ...val,
      progress: (val.mentions / maxValue) * 100,
      share: (val.mentions / total) * 100,
    }));

    return {
      showHeader: true,
      records: formatChild,
      className: 'dbo-topic-keyword-box',
      columns: [
        {
          key: 'topic',
          header: `<span class="dbo-topic-keyword-box-child-title">
          ${formatMessage({
            id: 'Including the following Tier 2 Topics',
          })}</span>`,
          render: (cvm) =>
            `<div class="dbo-topic-keyword-box-child">
              <span class="child-topic">${translationFunc(
                cvm.topic,
                'L2',
              )}</span>
              <div class="tea-progress">
                <div class="tea-progress__value" style="width: ${percentageFormat(
                  cvm.progress,
                )}; transition: width 0.2s; height: 12px; background: ${
              itemStyle.color
            };"/>
              </div>
            </div>`,
        },
        {
          key: 'mentions',
          align: 'right',
          weight: 'bold',
          header: '',
          render: (cvm) => reviewsFormat(cvm.mentions),
        },
        {
          key: 'share',
          align: 'right',
          weight: 'bold',
          header: '',
          render: (cvm) => percentageFormat(cvm.share),
        },
      ],
    };
  }

  function getLevelTwoTopicKeyword(
    records: Array<any>,
    data: any,
    total: number,
  ) {
    if (topicLevel === 'L1') return getLevelOneTopicChild(data, total);
    const { topicData } = getTopicData(records);
    const { candidate_list, must_list, l1_topic, l2_topic } = topicData || {};
    const candidateListResult: Array<any> = dealwithKeywordListStr(
      candidate_list,
      (region, text, isLast) => text,
    );
    const mustListResult: Array<any> = dealwithKeywordListStr(
      must_list,
      (region, text, isLast) => text,
    );

    return {
      showHeader: true,
      className: 'topic-keyword-box',
      records: [
        { text: formatMessage({ id: 'Tier1Topic-1' }), value: l1_topic },
        {
          text: formatMessage({ id: 'Must Have Keywords' }),
          value: must_list ? mustListResult.join(', ') : '-',
        },
        {
          text: formatMessage({ id: 'Optional keywords' }),
          value: candidate_list ? candidateListResult.join(', ') : '-',
        },
      ],
      columns: [
        {
          key: 'text',
          header: `<div data-name="${l2_topic}" style="color: #6a96f5; cursor: pointer;">
          ${formatMessage({ id: 'Topic Definition' })}</div>`,
        },
        {
          key: 'value',
          align: 'left',
          header: ' ',
          render: (item: any) => {
            return `<div class="topic-definition-box">${item.value}</div>`;
          },
        },
      ],
    };
  }

  const topicRef = useRef('');
  const chartClick = useCallback(
    (params: any) => {
      const isFlag = topicRef.current === params.name;
      if (isFlag) {
        topicRef.current = '';
        return onClick?.({
          dataIndex: null,
          seriesIndex: null,
          seriesName: null,
          name: null,
          except_others: null,
          except_topic: null,
          translationData: null,
        });
      }
      topicRef.current = params.name;
      const exceptOthers = data
        ?.filter((item) => item.name !== 'Others')
        ?.map((item) => item.name);
      onClick?.({
        seriesIndex: params.seriesIndex,
        dataIndex: params.dataIndex,
        name: null,
        seriesName: params.name,
        except_others: params.name === 'Others' ? exceptOthers : null,
        except_topic: params.name === 'Others' ? exceptOthers : null,
        translationData: {
          ...translationData,
          others: {
            'zh-CN': '其他',
            'en-US': 'Others',
          },
        },
      });
    },
    [data, translationData],
  );

  const otherOptions = useCallback(
    (total: number) => ({
      type: 'treemap',
      width: '100%',
      height: '100%',
      left: '0px',
      right: '0px',
      top: '0px',
      bottom: '0px',
      label: {
        position: 'insideTopLeft',
        padding: [10, 0, 0, 10],
        overflow: 'truncate',
        formatter: (params: any) =>
          `{a|${translationFunc(params.name)}}\n\n{b|${reviewsFormat(
            params.value,
          )}}`,
        rich: {
          a: {
            color: '#FFFFFF',
            fontSize: 12,
          },
          b: {
            fontSize: 16,
            color: '#FFFFFF',
            width: '100%',
          },
        },
      },
      breadcrumb: { show: false },
      tooltip: {
        enterable: true,
        formatter: (params: any) => {
          const title = formatTimeToShow(filter);
          return formatChartParamsDate({
            params,
            total,
            className: 'block-chart-tooltip-box',
            listTitleFunc: (item: any) => translationFunc(item.name) || '',
            dealwithRecords(records, total) {
              const { topicData, key } = getTopicData(records);
              let { l2_topic } = topicData || {};
              if (topicLevel === 'L1') {
                l2_topic = key;
              }
              if (needToFeeds) {
                records.push({
                  text: `<div class="chart-common-formatter-links" data-url="goToFeeds" data-topic="${l2_topic?.toLocaleLowerCase()}"> ${formatMessage(
                    { id: 'Click to go to [Feeds]' },
                  )}</div>`,
                  value: '',
                  originValue: 0,
                  originText: 'Click to go to Feeds',
                });
              }

              return records.map((item) => ({
                ...item,
                share:
                  item.originText === 'Click to go to Feeds'
                    ? ''
                    : getPercentageInfo(item.originValue || 0, total, 2),
              }));
            },
            addExtraInfo: (element: any) => ({ originTopic: element.name }),
            tablesConfigFunc: (records: Array<any>) => {
              const bottomTable = getLevelTwoTopicKeyword(
                records,
                params.data,
                total,
              );

              const tablesConfig = [
                {
                  showHeader: true,
                  records,
                  columns: [
                    {
                      key: 'text',
                      header: title,
                    },
                    {
                      key: 'value',
                      align: 'right',
                      weight: 'bold',
                      header: formatMessage({ id: 'Mentions' }),
                    },
                    {
                      key: 'share',
                      align: 'right',
                      weight: 'bold',
                      header: formatMessage({ id: 'Share-1' }),
                    },
                  ],
                },
              ];
              if (bottomTable) {
                tablesConfig.push(bottomTable);
              }

              return tablesConfig;
            },
          });
        },
        confine: false,
        position(point, params, dom, rect, size) {
          return DBO_EC.position(
            point,
            params,
            dom,
            rect,
            size,
            0,
            15,
            'bottom',
            position === 'left' ? 1 : 4,
          );
        },
      },
      emphasis: {
        focus: 'series',
        blurScope: 'coordinateSystem',
      },
      select: {
        disabled: false,
        label: {
          borderColor: '#000000',
          borderWidth: 2,
        },
      },

      selectedMode: 'single',
      roam: 'false',
      nodeClick: 'false',
    }),
    [translationFunc, optionalKeywords, formatMessage],
  );

  const divElementRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function clickEventItem(event: MouseEvent) {
      const { target } = event;
      const name = target?.getAttribute('data-name');
      if (name) {
        openWindows(name);
        return;
      }
      const url = target?.getAttribute('data-url');
      const topic = target?.getAttribute('data-topic');
      if (url) {
        // Feeds 为特殊页面，需先请求 feeds 数据决定 tab，再走本地缓存下钻
        const feedFilter = {
          ...filter,
          ...gameInfo,
          topic: dealwithTopic(topic),
        };
        getFeedsStats(feedFilter).then((data: any) => {
          let tab = 'socialMedia';
          const { social_total, comments_total } = data || {};
          if (social_total === 0 && comments_total > 0) {
            tab = 'gameStore';
          }
          goPage({
            path: `/opinion/Feeds/Feeds?tab=${tab}`,
            filter: feedFilter,
            mode: EDrilldownMode.Local,
          });
        });
      }
    }

    if (divElementRef.current) {
      divElementRef.current.addEventListener('click', clickEventItem);
    }

    return () => {
      if (divElementRef.current) {
        divElementRef.current.removeEventListener('click', clickEventItem);
      }
    };
  }, [optionalKeywords]);

  const [options] = useMemo(() => {
    const series: any[] = [];
    const legend: any[] = [];
    data.forEach((item, index) => {
      if (['others', 'other'].includes(item.name.toLocaleLowerCase())) {
        return;
      }
      const color = chartAllColors[index];
      series.push({
        name: item.name,
        value: item.count,
        itemStyle: { color },
        child: item.child,
      });
      legend.push({
        name: item.name,
        itemStyle: { color },
      });
    });

    return [
      {
        grid: {
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
        },
        tooltip: {
          trigger: 'item',
          className: 'block-chart-tooltip-outer',
        },
        series: {
          data: series,
          ...otherOptions(total),
        },
      },
    ];
  }, [data, otherOptions, total]);

  return (
    <div ref={divElementRef}>
      <EChartCommon
        options={options}
        height={height}
        empty={data?.length === 0}
        loading={loading}
        optionsProps={{ type: 'treemap' }}
        eventsDict={{ click: chartClick }}
      ></EChartCommon>
      {KeywordsModalElement({ gameInfo, transTopic: translationFunc })}
    </div>
  );
}
